import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { AiMessage, StatusRow } from "@/zapi-frontend/shared/ui/chat";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { HOME_SLUG, OUTLINE_SLUG, type TLeoStatus } from "../lib/leoProgress";

type TLeoBusyMessageProps = {
  status: TLeoStatus | null;
  isChangingContent: boolean;
};

const chapterLabel = (t: TFunction<"projectHome">, { chapter, slug }: { chapter: string; slug: string }) =>
  slug === HOME_SLUG ? t("sidebar.home") : slug === OUTLINE_SLUG ? t("leo.slides") : chapter;

const statusText = (t: TFunction<"projectHome">, status: TLeoStatus | null): string => {
  switch (status?.kind) {
    case "reading":
      return t("leo.status.reading", { chapter: chapterLabel(t, status) });
    case "editing":
      return t("leo.status.editing", { chapter: chapterLabel(t, status) });
    case "readingSlide":
      return t("leo.status.readingSlide", { slide: status.slide });
    case "editingSlide":
      return t("leo.status.editingSlide", { slide: status.slide });
    case "designing":
      return t("leo.status.designing");
    case "searching":
      return t("leo.status.searching", { query: status.query });
    case "thinking":
      return t("leo.status.thinking");
    case "replying":
      return t("leo.status.replying");
    default:
      return t("leo.status.starting");
  }
};

// What Leo is doing right now, and once it has changed something, that the content on screen
// stays as it is until the update is ready.
export function LeoBusyMessage({ status, isChangingContent }: TLeoBusyMessageProps) {
  const { t } = useTranslation("projectHome");
  return (
    <AiMessage className="w-full">
      {isChangingContent && (
        <FigmaTypography variant="body-2" color="zinc900">
          {t("leo.updatingAnalysis")}
        </FigmaTypography>
      )}
      <StatusRow icon={<MagnifyingGlassIcon size={16} />} muted className={cn(isChangingContent && "mt-1.5")}>
        {statusText(t, status)}
      </StatusRow>
    </AiMessage>
  );
}
